import { customerBaseApi } from "@/react/services/api/api";
import {
  availableQuantityUrl,
  licenseAddressEditor,
  licensePoolsUrl,
  licensesBaseUrl,
} from "@/react/features/smp/licenses/urls";
import {
  LICENSE_POOLS,
  LICENSES,
} from "@/react/features/smp/licenses/constants";
import { KEY } from "@/react/components/table/useTableMutations";
import { licenseSchema } from "@/react/features/smp/licenses/schema";
import { formatAddress } from "@/react/features/smp/address-management/schema";
import { handleMutationResponse } from "@/react/services/api/handleMutationResponse";

export const licensesApi = customerBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    retrieveLicenses: builder.query({
      query: (filterOpts) => ({
        url: licensesBaseUrl,
        method: "POST",
        body: filterOpts,
      }),
      transformResponse: (response) =>
        response.map((license) => {
          if (license.serviceAddress) {
            license.serviceAddress = formatAddress(license.serviceAddress);
          }
          return license;
        }),
      providesTags: [LICENSES],
    }),
    deleteLicenses: builder.mutation({
      query: (licenseIds) => ({
        url: licensesBaseUrl,
        method: "DELETE",
        body: licenseIds,
      }),
      invalidatesTags: [LICENSES],
    }),
    retrieveLicensePools: builder.query({
      query: (filterOpts) => ({
        url: licensePoolsUrl,
        method: "POST",
        body: filterOpts,
      }),
      transformResponse: (response) => {
        const resultMap = {};
        for (const item of response) {
          resultMap[item.licensePoolId] = item;
        }
        return resultMap;
      },
      providesTags: [LICENSE_POOLS],
    }),
    retrieveAvailableSlots: builder.query({
      query: (filterOpts) => ({
        url: availableQuantityUrl,
        method: "POST",
        body: filterOpts,
      }),
    }),
    updateLicenseAddresses: builder.mutation({
      query: (licenseAddressIdPairs) => ({
        url: licenseAddressEditor,
        method: "PATCH",
        body: licenseAddressIdPairs,
      }),
      invalidatesTags: [LICENSES],
    }),
  }),
});

export const {
  useLazyRetrieveLicensesQuery,
  useRetrieveLicensePoolsQuery,
  useDeleteLicensesMutation,
  useRetrieveAvailableSlotsQuery,
  useUpdateLicenseAddressesMutation,
} = licensesApi;

export const handleUpdateResponse = ({
  rowsToUpdate,
  setRowsToUpdate,
  setData,
}) =>
  handleMutationResponse({
    rows: rowsToUpdate,
    setRows: setRowsToUpdate,
    setData,
    responseMappingKey: licenseSchema.fields.licenseId.spec.key,
    idKey: KEY,
    notifyContent: (successfulMutationCount, mutationCount) =>
      `${successfulMutationCount}/${mutationCount} license${
        mutationCount === 1 ? "" : "s"
      } updated`,
    isVoidResponse: true,
  });

export const handleDeleteResponse = ({
  rowsToDelete,
  setRowsToDelete,
  setData,
}) =>
  handleMutationResponse({
    rows: rowsToDelete,
    setRows: setRowsToDelete,
    setData,
    responseMappingKey: licenseSchema.fields.licenseId.spec.key,
    idKey: KEY,
    notifyContent: (successfulMutationCount, mutationCount) =>
      `${successfulMutationCount}/${mutationCount} license${
        mutationCount === 1 ? "" : "s"
      } deleted`,
    isVoidResponse: true,
  });
